/**
 * ARIA Live Region Utilities
 * 
 * Utilities for managing ARIA live regions to announce dynamic content
 * changes to screen readers.
 * 
 * Requirements: 8.5 - Screen reader announcements for dynamic content
 */

export type AriaLivePoliteness = 'polite' | 'assertive' | 'off';

/**
 * Create or get an ARIA live region element
 */
const getOrCreateLiveRegion = (
  politeness: AriaLivePoliteness = 'polite'
): HTMLElement => {
  const id = `aria-live-${politeness}`;
  let region = document.getElementById(id);

  if (!region) {
    region = document.createElement('div');
    region.id = id;
    region.setAttribute('aria-live', politeness);
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
    region.style.position = 'absolute';
    region.style.left = '-10000px';
    region.style.width = '1px';
    region.style.height = '1px';
    region.style.overflow = 'hidden';
    document.body.appendChild(region);
  }

  return region;
};

/**
 * Announce a message to screen readers
 * 
 * @param message - The message to announce
 * @param politeness - The politeness level ('polite' or 'assertive')
 * @param clearDelay - Time in ms before clearing the message (default: 1000)
 */
export const announce = (
  message: string,
  politeness: AriaLivePoliteness = 'polite',
  clearDelay: number = 1000
): void => {
  const region = getOrCreateLiveRegion(politeness);
  
  // Clear any existing content
  region.textContent = '';
  
  // Use setTimeout to ensure the screen reader picks up the change
  setTimeout(() => {
    region.textContent = message;
    
    // Clear the message after the specified delay
    if (clearDelay > 0) {
      setTimeout(() => {
        region.textContent = '';
      }, clearDelay);
    }
  }, 100);
};

/**
 * Announce a polite message (doesn't interrupt current speech)
 */
export const announcePolite = (message: string, clearDelay?: number): void => {
  announce(message, 'polite', clearDelay);
};

/**
 * Announce an assertive message (interrupts current speech)
 */
export const announceAssertive = (message: string, clearDelay?: number): void => {
  announce(message, 'assertive', clearDelay);
};

/**
 * Clear all live region announcements
 */
export const clearAnnouncements = (): void => {
  ['polite', 'assertive'].forEach((politeness) => {
    const region = document.getElementById(`aria-live-${politeness}`);
    if (region) {
      region.textContent = '';
    }
  });
};

/**
 * Initialize ARIA live regions on app startup
 */
export const initializeAriaLiveRegions = (): void => {
  getOrCreateLiveRegion('polite');
  getOrCreateLiveRegion('assertive');
};
